import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { STATUS } from "@/components/AppointmentTable";
import { Clock, Eye, ClipboardPen, CalendarCheck, CheckCircle2, Stethoscope } from "lucide-react";

const sameDay = (a, b) => a.toDateString() === b.toDateString();
const fmtTime = (s) => new Date(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
const fmtDay = (s) => new Date(s).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

const Stat = ({ icon: Icon, label, value, tint }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
    <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${tint}`}><Icon className="h-6 w-6" /></div>
    <div><div className="text-xs uppercase tracking-wider text-slate-400">{label}</div><div className="font-head text-2xl font-bold text-slate-900">{value}</div></div>
  </div>
);

const Row = ({ a, showDay, onReceipt, onClinical }) => (
  <div data-testid="today-appt-item" className="flex items-center gap-4 border border-slate-200 rounded-xl p-4 bg-white hover:bg-slate-50">
    <div className="flex flex-col items-center w-20 shrink-0">
      <Clock className="h-4 w-4 text-teal-600 mb-1" />
      <span className="font-mono text-sm font-semibold text-slate-800">{fmtTime(a.appointment_date)}</span>
      {showDay && <span className="text-[11px] text-slate-400">{fmtDay(a.appointment_date)}</span>}
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-medium text-slate-900">{a.patient_name} <span className="text-slate-400 text-sm">• {a.patient_age} yrs</span></div>
      <div className="text-sm text-slate-500 truncate">{a.disease_name || "No diagnosis yet"}{a.diagnosis ? ` — ${a.diagnosis}` : ""}</div>
    </div>
    <Badge variant="outline" className={`capitalize ${STATUS[a.status] || ""}`}>{a.status}</Badge>
    <Button size="sm" variant="outline" onClick={() => onClinical(a)} data-testid="today-clinical-button"><ClipboardPen className="h-4 w-4 mr-1" /> Notes</Button>
    <Button size="icon" variant="ghost" onClick={() => onReceipt(a)} title="View receipt"><Eye className="h-4 w-4" /></Button>
  </div>
);

export default function DoctorToday({ appointments, doctor, onReceipt, onClinical }) {
  const now = new Date();
  const { today, upcoming } = useMemo(() => {
    const sorted = [...appointments].sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));
    return {
      today: sorted.filter((a) => sameDay(new Date(a.appointment_date), now)),
      upcoming: sorted.filter((a) => new Date(a.appointment_date) > now && !sameDay(new Date(a.appointment_date), now)).slice(0, 10),
    };
  }, [appointments]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = today.filter((a) => a.status === "completed").length;

  return (
    <div className="space-y-8" data-testid="doctor-today">
      <div className="bg-[#0F172A] text-white rounded-3xl p-6 sm:p-8 flex items-center gap-5">
        {doctor?.avatar && <img src={doctor.avatar} alt={doctor.name} className="h-16 w-16 rounded-2xl object-cover ring-2 ring-teal-500/40" />}
        <div>
          <div className="text-teal-300 text-sm">Good day,</div>
          <h2 className="font-head text-2xl sm:text-3xl font-bold">{doctor?.name || "Doctor"}</h2>
          <div className="text-slate-400 text-sm flex items-center gap-2 mt-1"><Stethoscope className="h-4 w-4" /> {doctor?.specialty} · {now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Stat icon={CalendarCheck} label="Today's Patients" value={today.length} tint="bg-teal-50 text-teal-600" />
        <Stat icon={CheckCircle2} label="Completed Today" value={done} tint="bg-blue-50 text-blue-600" />
        <Stat icon={Clock} label="Upcoming" value={upcoming.length} tint="bg-amber-50 text-amber-600" />
      </div>

      <section>
        <h3 className="font-head text-lg font-semibold mb-3">Today's Schedule</h3>
        {today.length === 0
          ? <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400">No appointments scheduled for today.</div>
          : <div className="space-y-3">{today.map((a) => <Row key={a.id} a={a} onReceipt={onReceipt} onClinical={onClinical} />)}</div>}
      </section>

      <section>
        <h3 className="font-head text-lg font-semibold mb-3">Upcoming</h3>
        {upcoming.length === 0
          ? <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400">Nothing scheduled ahead.</div>
          : <div className="space-y-3">{upcoming.map((a) => <Row key={a.id} a={a} showDay onReceipt={onReceipt} onClinical={onClinical} />)}</div>}
      </section>
    </div>
  );
}
